import { api } from '@/lib/api'
import { auth } from '@/lib/firebase'
import { env } from '@/lib/env'
import { demoAssistantStream, isDemo } from '@/lib/demo'

const BASE = `${env.apiBaseUrl}/api/v1/ai-assistant`

export type StreamData = { type: 'message'; content: string } | null

export interface StreamBody {
  chat_id?: number
  context: { current_user_location: string }
  data?: StreamData
}

export interface ToolEnd {
  callId: string
  name: string
  arguments?: Record<string, unknown>
  displayName?: string
  accessLevel?: string
  summary?: string
}

export type StreamEvent = { type: 'meta'; id: number } | { type: 'chunk'; text: string } | { type: 'tool'; call: ToolEnd }

/**
 * POST /chat/stream and read its server-sent events: `chat-metadata`, then
 * `message` chunks, then one `tool-end` per requested tool call, then
 * `message: [DONE]`. Uses fetch, since axios can't stream a response body.
 */
export async function streamChat(body: StreamBody, signal: AbortSignal, onEvent: (e: StreamEvent) => void) {
  let res: Response
  if (isDemo()) {
    res = demoAssistantStream(body, signal)
  } else {
    const token = await auth.currentUser?.getIdToken()
    res = await fetch(`${BASE}/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal,
    })
  }
  if (!res.ok || !res.body) throw new Error(`Assistant stream failed (${res.status})`)

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  let event = 'message'
  const handle = (data: string) => {
    if (event === 'message') {
      if (data === '[DONE]') return
      const parsed = JSON.parse(data) as { chunk?: string }
      if (parsed.chunk) onEvent({ type: 'chunk', text: parsed.chunk })
    } else if (event === 'tool-end') {
      onEvent({ type: 'tool', call: JSON.parse(data) })
    } else if (event === 'chat-metadata') {
      const meta = JSON.parse(data) as { id?: number }
      if (meta.id) onEvent({ type: 'meta', id: meta.id })
    }
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += value
    let nl: number
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).replace(/\r$/, '')
      buffer = buffer.slice(nl + 1)
      if (line === '') event = 'message'
      else if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) {
        try {
          handle(line.slice(5).trimStart())
        } catch {
          /* a malformed event shouldn't end the stream */
        }
      }
    }
  }
}

export interface ExecuteResult {
  success: boolean
  status: 'completed' | 'failed' | 'denied'
  result?: Record<string, unknown> | null
  error?: string | null
}

export async function executeTool(callId: string, approval: number) {
  return (await api.post<ExecuteResult>('/ai-assistant/tool/execute', { call: { id: callId, approval_status: approval } })).data
}

export interface HistoryItem {
  id: number
  payload: {
    role?: string
    content?: string
    type?: 'function_call' | 'function_call_output'
    call_id?: string
    name?: string
    arguments?: string
    output?: string
  }
  call_approval: number
  created_at: string
}

export async function getHistory() {
  try {
    return (await api.get<{ history: HistoryItem[] }>('/ai-assistant/chat/history')).data.history ?? []
  } catch (e) {
    // no chat yet
    if ((e as { response?: { status?: number } }).response?.status === 404) return []
    throw e
  }
}

/** The API deletes every chat of this user in this team; there's one conversation at a time. */
export const deleteChats = () => api.delete('/ai-assistant/chat')
